// import React, { createContext, useContext, useState, useEffect } from "react";
// import type { ReactNode } from "react";

// interface AuthContextType {
//   isAuthenticated: boolean;
//   login: (email: string, password: string) => Promise<void>;
//   logout: () => void;
// }

// const AuthContext = createContext<AuthContextType | undefined>(undefined);

// const AUTH_STORAGE_KEY = "isAuthenticated";

// export const AuthProvider = ({ children }: { children: ReactNode }) => {
//   const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
//     const stored = localStorage.getItem(AUTH_STORAGE_KEY);
//     return stored === "true";
//   });

//   useEffect(() => {
//     localStorage.setItem(AUTH_STORAGE_KEY, String(isAuthenticated));
//   }, [isAuthenticated]);

//   const login = async (email: string, password: string) => {
//     if (email === "admin" && password === "admin") {
//       setIsAuthenticated(true);
//     } else {
//       throw new Error("Invalid credentials");
//     }
//   };

//   const logout = () => {
//     setIsAuthenticated(false);
//     localStorage.removeItem(AUTH_STORAGE_KEY);
//   };

//   return (
//     <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
//       {children}
//     </AuthContext.Provider>
//   );
// };

// export const useAuth = () => {
//   const ctx = useContext(AuthContext);
//   if (!ctx) throw new Error("useAuth must be used within AuthProvider");
//   return ctx;
// };



// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../shared/authService";
import { getChannelList } from "../shared/matchingRuleService";
import { getUserDetailsByID } from "../shared/SystemAdministrationService";


// User interface matching backend UserResponse
interface User {
  id: number;
  f_name?: string;
  m_name?: string;
  l_name?: string;
  gender?: string;
  phone?: string;
  birth_date?: string;
  email?: string;
  username?: string;
  role?: string;
  status?: boolean;
  created_at?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  roleDetails:any;
  channelsList:any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [channelsList, setChannelList] = useState<any | null>(null);
  const [roleDetails, setUserRoleDetails] = useState<any | null>(null);

  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user and token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
    const storedUser = localStorage.getItem("user");
    const storedChannelList = localStorage.getItem("channels_list");
    const storedUserRoleDetails = localStorage.getItem("user_role_details");

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setChannelList(storedChannelList);
      setUserRoleDetails(storedUserRoleDetails);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Check if access_token or user was removed
      if (e.key === "access_token" || e.key === "user" || e.key === "channels_list") {
        const storedToken = localStorage.getItem("access_token");
        const storedUser = localStorage.getItem("user");
        const storedChannels = localStorage.getItem("channels_list");
        const userRoleDetails = localStorage.getItem("user_role_details");
        
        // If token or user is removed, logout
        if (!storedToken || !storedUser || !storedChannels || userRoleDetails) {
          setToken(null);
          setUser(null);
          setChannelList(null);
          setUserRoleDetails(null);
        } else {
          // Update token and user if changed
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setChannelList(storedChannels);
          setUserRoleDetails(userRoleDetails);
        }
      }
      
      // If entire localStorage is cleared
      if (e.key === null) {
        setToken(null);
        setUser(null);
        setChannelList(null);
        setUserRoleDetails(null);
      }
    };

    // Add event listener for storage changes
    window.addEventListener("storage", handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password);
      // const userResponce = JSON.stringify(response.user);
      // console.log('userResponce',userResponce);
      const channelList = await getChannelList();
      // console.log(response?.user?.id)
      const userDetails = await getUserDetailsByID(response?.user?.id)
      // console.log('userDetails?.data',userDetails?.data);
      // Save token and user to state
      setToken(response.access_token);
      setUser(response.user);
      // console.log('channelList',channelList?.result?.data);

      // Save to localStorage
      localStorage.setItem("channels_list", JSON.stringify(channelList?.result?.data));
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("user", JSON.stringify(response.user));
      localStorage.setItem("user_role_details", JSON.stringify(userDetails?.data[0]))
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    // Clear state
    setUser(null);
    setToken(null);

    // Clear localStorage
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("channels_list");
    localStorage.removeItem("user_role_details");

    // Optional: Call backend logout API
    if (token) {
      authAPI.logout().catch((err:any) => console.error("Logout API error:", err));
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    isLoading,
    channelsList,
    roleDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
